import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

// Storage helpers — Lotto 3 #6 "Ricordami".
// When the user checks "Remember me" at login, the JWT is persisted in
// localStorage (survives tab close / browser restart). When unchecked, the
// token is stored in sessionStorage, which is automatically wiped when the
// tab is closed. The backend never sees the difference: it's the same JWT
// with the same expiry, we just choose where it lives on the client.
const TOKEN_KEY = "tc_token";
const REMEMBER_KEY = "tc_remember";

function _saveToken(token, remember) {
  try {
    // Always wipe the other bucket so we don't have two conflicting copies.
    try { localStorage.removeItem(TOKEN_KEY); } catch {}
    try { sessionStorage.removeItem(TOKEN_KEY); } catch {}
    if (remember) {
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(REMEMBER_KEY, "1");
    } else {
      sessionStorage.setItem(TOKEN_KEY, token);
      try { localStorage.removeItem(REMEMBER_KEY); } catch {}
    }
  } catch { /* storage disabled — silent fallback */ }
}

function _clearToken() {
  try { localStorage.removeItem(TOKEN_KEY); } catch {}
  try { sessionStorage.removeItem(TOKEN_KEY); } catch {}
  try { localStorage.removeItem(REMEMBER_KEY); } catch {}
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      // Session expired or invalid token: wipe temporary session state so the
      // next user (or new session) starts with a clean slate.
      try { sessionStorage.removeItem("tc_map_filters"); } catch {}
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, skip /me check — AuthCallback handles it
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = async (email, password, remember = true) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Wipe temporary session-scoped state (map filters, etc.) so a new account
    // never inherits filters from the previous one on the same browser tab.
    try { sessionStorage.removeItem("tc_map_filters"); } catch {}
    _saveToken(data.token, remember);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    try { sessionStorage.removeItem("tc_map_filters"); } catch {}
    // Fresh registration → default to "remember" so the user isn't forced to
    // sign in again after closing the tab post-onboarding.
    _saveToken(data.token, true);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    // iter82 — best-effort: tell FCM to stop sending push to this device.
    // Never block logout if the request fails.
    try {
      const t = localStorage.getItem("push_token");
      if (t) await api.post("/push/unregister-token", { token: t, platform: "web" });
    } catch {}
    try { localStorage.removeItem("push_token"); } catch {}
    try { await api.post("/auth/logout"); } catch {}
    try { sessionStorage.removeItem("tc_map_filters"); } catch {}
    _clearToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
